import { useEsignRequestDecline } from "@/queries/use-esign.query.ts";
import {
  Button,
  Checkbox,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Stack,
  Text,
  Textarea,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const DECLINE_REASON_MAX_LENGTH = 2000;

export interface EsignDeclineModalProps extends ModalProps {
  requestId: string;
  withdrawConsentByDefault?: boolean;
  onDeclined?: () => void;
}

export function EsignDeclineModal({
  requestId,
  withdrawConsentByDefault = false,
  onDeclined,
  ...props
}: EsignDeclineModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { showInfo } = useToasts();
  const { id } = props;
  const { mutate: declineRequest, isPending } = useEsignRequestDecline();

  const [reason, setReason] = useState("");
  const [withdrawConsent, setWithdrawConsent] = useState(withdrawConsentByDefault);

  const handleDecline = () => {
    if (isPending) return;
    declineRequest(
      {
        requestId,
        payload: { reason: reason.trim() || null, withdraw_consent: withdrawConsent },
      },
      {
        onSuccess: () => {
          close(id);
          showInfo({
            title: t("core:esign_declined_toast"),
            icon: <FontAwesomeIcon icon={"fa-light fa-check"} />,
          });
          onDeclined?.();
        },
      }
    );
  };

  return (
    <Modal
      {...props}
      title={t("core:esign_decline_title")}
      variant={"middle"}
      size={"md"}
      withCloser={true}
      actions={
        <Stack gap={"sm"}>
          <Button size={"lg"} isLoading={isPending} disabled={isPending} onClick={handleDecline}>
            {t("core:esign_decline_confirm")}
          </Button>
          <Button variant={"plain"} size={"md"} onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"lg"}>
        <Text className={"text-secondary"}>{t("core:esign_decline_description")}</Text>
        <Textarea
          label={t("core:esign_decline_reason_label")}
          value={reason}
          maxLength={DECLINE_REASON_MAX_LENGTH}
          onChange={(e) => setReason(e.target.value)}
        />
        <Checkbox
          checked={withdrawConsent}
          onChange={(e) => setWithdrawConsent(e.target.checked)}
          title={t("core:esign_decline_withdraw_consent")}
        />
      </Stack>
    </Modal>
  );
}
